import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete
} from '@mui/material';
import {
  Psychology,
  Security,
  Warning,
  TrendingUp,
  SmartToy,
  Insights,
  MonitorHeart,
  Pattern,
  Business,
  Devices,
  Analytics as AnalyticsIcon,
  BarChart
} from '@mui/icons-material';
import adminApi from '../services/api';
import {
  VendorResponse,
  VendorAnalyticsResponse
} from '../types/api';
import UniqueDeviceAnalytics from './UniqueDeviceAnalytics';

interface AnalyticsData {
  health: any;
  securityInsights: any;
  mlStatus: any;
  dashboardSummary: any;
  anomalyAlerts: any[];
}

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [vendors, setVendors] = useState<VendorResponse[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorResponse | null>(null);
  const [vendorAnalytics, setVendorAnalytics] = useState<VendorAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('week');

  useEffect(() => {
    loadAnalyticsData();
    loadVendors();
  }, []);

  useEffect(() => {
    if (selectedVendor && activeTab === 1) {
      loadVendorAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor, dateRange, activeTab]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [health, securityInsights, mlStatus, dashboardSummary, anomalyAlerts] = await Promise.all([
        adminApi.getAnalyticsHealth(),
        adminApi.getSecurityInsights(),
        adminApi.getMLModelStatus(),
        adminApi.getAnalyticsDashboardSummary(),
        adminApi.getAnomalyAlerts({ hours: 24 })
      ]);

      setData({
        health: health.health,
        securityInsights: securityInsights.insights,
        mlStatus: mlStatus.ml_status,
        dashboardSummary: dashboardSummary.summary,
        anomalyAlerts: anomalyAlerts.alerts
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load analytics data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await adminApi.getVendors({ active_only: true });
      setVendors(response.vendors || []);
      if (response.vendors && response.vendors.length > 0) {
        setSelectedVendor(response.vendors[0]);
      }
    } catch (err: any) {
      console.error('Failed to load vendors:', err);
    }
  };

  const loadVendorAnalytics = useCallback(async () => {
    if (!selectedVendor) return;

    try {
      setVendorLoading(true);
      const dateRangeData = adminApi.getDateRange(dateRange as 'today' | 'week' | 'month' | 'quarter');
      
      const analytics = await adminApi.getVendorAnalytics({
        vendor_id: selectedVendor.vendor_id,
        start_date: dateRangeData.start_date,
        end_date: dateRangeData.end_date,
        include_device_breakdown: true,
        include_app_breakdown: true,
        group_by: dateRange === 'today' ? 'hour' : 'day'
      });
      
      setVendorAnalytics(analytics);
    } catch (err: any) {
      console.error('Failed to load vendor analytics:', err);
      setVendorAnalytics(null);
    } finally {
      setVendorLoading(false);
    }
  }, [selectedVendor, dateRange]);

  const handleInitializeML = async () => {
    try {
      setActionLoading('initialize');
      await adminApi.initializeMLModels();
      await loadAnalyticsData(); // Refresh data
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize ML models';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDetectPatterns = async () => {
    try {
      setActionLoading('patterns');
      const result = await adminApi.detectPatterns();
      alert(`Pattern detection complete! Found ${result.patterns_detected} patterns.`);
      await loadAnalyticsData(); // Refresh data
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to detect patterns';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartMonitoring = async () => {
    try {
      setActionLoading('monitoring');
      await adminApi.startContinuousMonitoring();
      alert('Continuous monitoring started!');
      await loadAnalyticsData(); // Refresh data
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start monitoring';
      setError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      case 'critical': return '#9c27b0';
      default: return '#757575';
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatPercentage = (value: number, total: number) => {
    if (total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadAnalyticsData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        📊 Analytics & Intelligence
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="System Analytics" icon={<AnalyticsIcon />} />
          <Tab label="Vendor Analytics" icon={<Business />} />
          <Tab label="Device Growth" icon={<Devices />} />
        </Tabs>
      </Card>

      {/* System Analytics Tab */}
      {activeTab === 0 && data && (
        <>
      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<SmartToy />}
          onClick={handleInitializeML}
          disabled={actionLoading === 'initialize'}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          {actionLoading === 'initialize' ? <CircularProgress size={20} /> : 'Initialize ML Models'}
        </Button>
        <Button
          variant="contained"
          startIcon={<Pattern />}
          onClick={handleDetectPatterns}
          disabled={actionLoading === 'patterns'}
          sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}
        >
          {actionLoading === 'patterns' ? <CircularProgress size={20} /> : 'Detect Patterns'}
        </Button>
        <Button
          variant="contained"
          startIcon={<MonitorHeart />}
          onClick={handleStartMonitoring}
          disabled={actionLoading === 'monitoring'}
          sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}
        >
          {actionLoading === 'monitoring' ? <CircularProgress size={20} /> : 'Start Monitoring'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<TrendingUp />}
          onClick={loadAnalyticsData}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>ML Models</Typography>
                  <Typography variant="h4" fontWeight="bold">
                        {data.mlStatus?.anomaly_model_loaded ? '✅ Active' : '❌ Inactive'}
                  </Typography>
                </Box>
                <Psychology sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Threat Level</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                        {data.dashboardSummary?.threat_level || 'Unknown'}
                  </Typography>
                </Box>
                <Security sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Alerts (24h)</Typography>
                  <Typography variant="h4" fontWeight="bold">
                        {data.dashboardSummary?.alerts_24h || 0}
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom>Service Status</Typography>
                  <Typography variant="h4" fontWeight="bold">
                        {data.health?.service_status === 'healthy' ? '✅ Healthy' : '❌ Error'}
                  </Typography>
                </Box>
                <Insights sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Status */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToy /> ML Model Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Anomaly Model</Typography>
                  <Chip 
                        label={data.mlStatus?.anomaly_model_loaded ? 'Loaded' : 'Not Loaded'} 
                        color={data.mlStatus?.anomaly_model_loaded ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Feature Scaler</Typography>
                  <Chip 
                        label={data.mlStatus?.scaler_loaded ? 'Loaded' : 'Not Loaded'} 
                        color={data.mlStatus?.scaler_loaded ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Alerts Tracked</Typography>
                      <Typography variant="h6">{data.mlStatus?.total_alerts_tracked || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Device Profiles</Typography>
                      <Typography variant="h6">{data.mlStatus?.device_profiles_cached || 0}</Typography>
                </Grid>
              </Grid>
                  {data.mlStatus?.recommendation && (
                <Alert severity="info" sx={{ mt: 2 }}>
                      {data.mlStatus.recommendation}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security /> Security Insights
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Threat Level</Typography>
                  <Chip 
                        label={data.dashboardSummary?.threat_level?.toUpperCase() || 'UNKNOWN'} 
                        color={getThreatLevelColor(data.dashboardSummary?.threat_level)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Active Alerts</Typography>
                      <Typography variant="h6">{data.securityInsights?.active_alerts || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">High Risk Devices</Typography>
                  <Typography variant="h6">
                        {Object.values(data.dashboardSummary?.device_risk_scores || {}).filter((score: any) => score > 0.7).length}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Anomaly Trends (7d)</Typography>
                      <Typography variant="h6">{data.dashboardSummary?.anomaly_trends?.alerts_last_7_days || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert Severity Distribution */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Alert Severity Distribution</Typography>
                  {data.dashboardSummary?.alerts_by_severity && (
                <Box>
                      {Object.entries(data.dashboardSummary.alerts_by_severity).map(([severity, count]: [string, any]) => (
                    <Box key={severity} sx={{ mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {severity}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                            value={Math.min(100, (count / Math.max(1, data.dashboardSummary.alerts_24h)) * 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getSeverityColor(severity)
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Alert Type Distribution</Typography>
                  {data.dashboardSummary?.alerts_by_type && Object.keys(data.dashboardSummary.alerts_by_type).length > 0 ? (
                <Box>
                      {Object.entries(data.dashboardSummary.alerts_by_type).map(([type, count]: [string, any]) => (
                    <Box key={type} sx={{ mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {type.replace('_', ' ')}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                            value={Math.min(100, (count / Math.max(1, data.dashboardSummary.alerts_24h)) * 100)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No alert type data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Anomaly Alerts */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Anomaly Alerts</Typography>
                  {data.anomalyAlerts && data.anomalyAlerts.length > 0 ? (
                <List>
                      {data.anomalyAlerts.slice(0, 10).map((alert, index) => (
                    <React.Fragment key={alert.alert_id || index}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={alert.severity?.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: getSeverityColor(alert.severity),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                              <Typography variant="body1" fontWeight="bold">
                                {alert.alert_type?.replace('_', ' ').toUpperCase()}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {alert.description}
                              </Typography>
                              <Box display="flex" gap={2} flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">
                                  Confidence: {(alert.confidence_score * 100).toFixed(1)}%
                                </Typography>
                                {alert.device_id && (
                                  <Typography variant="caption" color="text.secondary">
                                    Device: {alert.device_id}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(alert.detected_at).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                          {index < data.anomalyAlerts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No recent alerts detected
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Security Recommendations</Typography>
                  {data.dashboardSummary?.recommendations && data.dashboardSummary.recommendations.length > 0 ? (
                <List>
                      {data.dashboardSummary.recommendations.map((recommendation: string, index: number) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <Typography variant="body2" sx={{ color: 'primary.main' }}>
                              💡
                            </Typography>
                            <Typography variant="body2">
                              {recommendation}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recommendations at this time
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </>
      )}

      {/* Vendor Analytics Tab */}
      {activeTab === 1 && (
        <>
          {/* Vendor Selection and Controls */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={vendors}
                getOptionLabel={(vendor) => `${vendor.vendor_name} (${vendor.vendor_code})`}
                value={selectedVendor}
                onChange={(_, value) => setSelectedVendor(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Vendor"
                    helperText="Choose a vendor to view analytics"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                  <MenuItem value="quarter">Last 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TrendingUp />}
                onClick={loadVendorAnalytics}
                disabled={!selectedVendor}
                sx={{ height: '56px' }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          {selectedVendor ? (
            vendorLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={60} />
              </Box>
            ) : vendorAnalytics ? (
              <>
                {/* Vendor Overview Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business color="primary" />
                          {vendorAnalytics.vendor_name}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Vendor Code</Typography>
                            <Chip label={vendorAnalytics.vendor_code} size="small" variant="outlined" />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Environment</Typography>
                            <Chip 
                              label={vendorAnalytics.environment} 
                              size="small"
                              color={vendorAnalytics.environment === 'production' ? 'error' : 'info'}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">Period</Typography>
                            <Typography variant="body1">
                              {new Date(vendorAnalytics.period_start).toLocaleDateString()} - {new Date(vendorAnalytics.period_end).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5" fontWeight="bold">
                              {formatNumber(vendorAnalytics.total_requests)}
                            </Typography>
                            <Typography variant="caption">Total Requests</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Security sx={{ fontSize: 32, mb: 1 }} />
                            <Typography variant="h5" fontWeight="bold">
                              {formatPercentage(vendorAnalytics.successful_requests, vendorAnalytics.total_requests)}
                            </Typography>
                            <Typography variant="caption">Success Rate</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Request Metrics */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Request Statistics</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Total Requests</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatNumber(vendorAnalytics.total_requests)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Successful</Typography>
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                {formatNumber(vendorAnalytics.successful_requests)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Failed</Typography>
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {formatNumber(vendorAnalytics.failed_requests)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Success Rate</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatPercentage(vendorAnalytics.successful_requests, vendorAnalytics.total_requests)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Device & App Usage</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Unique Devices</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatNumber(vendorAnalytics.unique_devices)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Unique Apps</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {formatNumber(vendorAnalytics.unique_apps)}
                              </Typography>
                            </Box>
                          </Grid>
                          {vendorAnalytics.attestation_success_rate !== undefined && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Attestation Rate</Typography>
                                <Typography variant="body2" fontWeight="bold" color="info.main">
                                  {(vendorAnalytics.attestation_success_rate * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Rate Limit Violations</Typography>
                              <Typography variant="body2" fontWeight="bold" color="warning.main">
                                {formatNumber(vendorAnalytics.rate_limit_violations)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                        <Grid container spacing={2}>
                          {vendorAnalytics.avg_response_time_ms && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">Avg Response Time</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {vendorAnalytics.avg_response_time_ms.toFixed(1)}ms
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {vendorAnalytics.p95_response_time_ms && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">95th Percentile</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {vendorAnalytics.p95_response_time_ms.toFixed(1)}ms
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Error Rate</Typography>
                              <Typography variant="body2" fontWeight="bold" color="error.main">
                                {formatPercentage(vendorAnalytics.failed_requests, vendorAnalytics.total_requests)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Device Breakdown */}
                {vendorAnalytics.device_analytics && vendorAnalytics.device_analytics.length > 0 && (
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Devices />
                            Device Performance Breakdown
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Device Name</TableCell>
                                  <TableCell align="right">Total Requests</TableCell>
                                  <TableCell align="right">Success Rate</TableCell>
                                  <TableCell align="right">Unique Apps</TableCell>
                                  <TableCell align="right">Rate Limit Violations</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {vendorAnalytics.device_analytics.map((device: any, index: number) => (
                                  <TableRow key={index}>
                                    <TableCell>{device.device_name || `Device ${device.device_id}`}</TableCell>
                                    <TableCell align="right">{formatNumber(device.total_requests || 0)}</TableCell>
                                    <TableCell align="right">
                                      <Typography 
                                        variant="body2" 
                                        color={device.success_rate > 0.9 ? 'success.main' : 'warning.main'}
                                      >
                                        {formatPercentage(device.successful_requests || 0, device.total_requests || 0)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">{device.unique_apps || 0}</TableCell>
                                    <TableCell align="right">
                                      <Typography 
                                        variant="body2" 
                                        color={device.rate_limit_violations > 0 ? 'error.main' : 'text.primary'}
                                      >
                                        {device.rate_limit_violations || 0}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* App Breakdown */}
                {vendorAnalytics.app_analytics && vendorAnalytics.app_analytics.length > 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BarChart />
                            Application Usage Breakdown
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Application</TableCell>
                                  <TableCell align="right">Total Requests</TableCell>
                                  <TableCell align="right">Success Rate</TableCell>
                                  <TableCell align="right">Devices Used</TableCell>
                                  <TableCell align="right">First Used</TableCell>
                                  <TableCell align="right">Last Used</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {vendorAnalytics.app_analytics.map((app: any, index: number) => (
                                  <TableRow key={index}>
                                    <TableCell>{app.package_name || 'Unknown App'}</TableCell>
                                    <TableCell align="right">{formatNumber(app.total_requests || 0)}</TableCell>
                                    <TableCell align="right">
                                      <Typography 
                                        variant="body2" 
                                        color={app.success_rate > 0.9 ? 'success.main' : 'warning.main'}
                                      >
                                        {formatPercentage(app.successful_requests || 0, app.total_requests || 0)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">{app.unique_devices || 0}</TableCell>
                                    <TableCell align="right">
                                      {app.first_used ? new Date(app.first_used).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {app.last_used ? new Date(app.last_used).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </>
            ) : (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">
                    No analytics data available for this vendor in the selected date range
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={loadVendorAnalytics} 
                    sx={{ mt: 2 }}
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  Select a vendor to view analytics
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Device Growth Tab */}
      {activeTab === 2 && (
        <UniqueDeviceAnalytics dateRange={dateRange} />
      )}
    </Box>
  );
};

export default Analytics; 